<template>
    <b-modal
        :id="modalId"
        :title="title"
        size="xl"
        hide-footer
        no-close-on-backdrop
        no-enforce-focus
        @show="onModalShow"
        @hide="onBeforeHide"
        @hidden="onModalHidden">

        <!-- Unsaved Changes Warning Modal -->
        <b-modal
            :id="`${modalId}-unsaved-warning`"
            title="Unsaved Changes"
            ok-title="Discard Changes"
            ok-variant="danger"
            cancel-title="Go Back"
            @ok="discardAndClose"
            @cancel="showUnsavedWarning = false"
            @hidden="showUnsavedWarning = false"
            v-model="showUnsavedWarning"
            centered>
            <p>You have unsaved changes to this PDF. Are you sure you want to close without saving?</p>
        </b-modal>

        <div v-if="loading" class="text-center p-5">
            <i class="fas fa-spinner fa-spin fa-3x"></i>
            <p class="mt-3">Loading PDF...</p>
        </div>

        <div v-else-if="error" class="alert alert-danger">
            {{ error }}
        </div>

        <div v-else>
            <!-- Toolbar -->
            <div class="annotation-toolbar mb-2 p-2 bg-white rounded d-flex flex-wrap align-items-center">
                <b-button-group size="sm" class="mr-3">
                    <b-button
                        :variant="currentTool === 'select' ? 'primary' : 'outline-dark'"
                        @click="setTool('select')"
                        title="Select">
                        <i class="fas fa-mouse-pointer"></i>
                    </b-button>
                    <b-button
                        v-if="hasAnnotatePermission"
                        :variant="currentTool === 'text' ? 'primary' : 'outline-dark'"
                        @click="setTool('text')"
                        title="Add Text">
                        <i class="fas fa-font"></i>
                    </b-button>
                    <b-button
                        v-if="hasAnnotatePermission"
                        :variant="currentTool === 'rect' ? 'primary' : 'outline-dark'"
                        @click="setTool('rect')"
                        title="Rectangle">
                        <i class="fas fa-square"></i>
                    </b-button>
                </b-button-group>

                <b-button
                    v-if="hasAnnotatePermission"
                    variant="outline-danger"
                    size="sm"
                    class="mr-3"
                    :disabled="!hasSelection"
                    @click="deleteSelected"
                    title="Delete Selected">
                    <i class="fas fa-trash"></i>
                </b-button>

                <!-- Layer controls -->
                <b-button-group v-if="hasAnnotatePermission && hasSelection" size="sm" class="mr-3">
                    <b-button variant="outline-dark" @click="bringToFront" title="Bring to Front">
                        <i class="fas fa-angle-double-up"></i>
                    </b-button>
                    <b-button variant="outline-dark" @click="bringForward" title="Bring Forward">
                        <i class="fas fa-angle-up"></i>
                    </b-button>
                    <b-button variant="outline-dark" @click="sendBackward" title="Send Backward">
                        <i class="fas fa-angle-down"></i>
                    </b-button>
                    <b-button variant="outline-dark" @click="sendToBack" title="Send to Back">
                        <i class="fas fa-angle-double-down"></i>
                    </b-button>
                </b-button-group>

                <div v-if="hasAnnotatePermission" class="d-flex align-items-center mr-3">
                    <label class="mb-0 mr-2 small">Color:</label>
                    <input
                        type="color"
                        v-model="strokeColor"
                        class="form-control form-control-sm color-picker"
                        @input="applyColorToSelection"
                        title="Stroke/Text Color" />
                </div>

                <div v-if="hasAnnotatePermission && showFontSizeControl" class="d-flex align-items-center mr-3">
                    <label class="mb-0 mr-2 small">Size:</label>
                    <b-form-select
                        v-model="fontSize"
                        :options="fontSizeOptions"
                        size="sm"
                        class="font-size-select"
                        @change="applyFontSizeToSelection" />
                </div>

                <div v-if="hasAnnotatePermission && currentTool === 'rect'" class="d-flex align-items-center mr-3">
                    <label class="mb-0 mr-2 small">Fill:</label>
                    <input
                        type="color"
                        v-model="fillColor"
                        class="form-control form-control-sm color-picker"
                        :disabled="noFill"
                        @input="applyFillToSelection" />
                    <b-form-checkbox v-model="noFill" class="ml-2 small" @change="applyFillToSelection">
                        No Fill
                    </b-form-checkbox>
                </div>

                <b-button
                    v-if="hasAnnotatePermission"
                    variant="outline-dark"
                    size="sm"
                    class="mr-3"
                    :disabled="!canUndo"
                    @click="undo"
                    title="Undo (Ctrl+Z)">
                    <i class="fas fa-undo"></i>
                </b-button>

                <!-- Edit Tools Separator -->
                <span v-if="hasAnnotatePermission && hasEditPermission" class="toolbar-separator mx-2"></span>

                <!-- Edit Tools -->
                <b-button-group v-if="hasEditPermission" size="sm" class="mr-3">
                    <b-button
                        variant="outline-dark"
                        :disabled="cropMode || reorderMode"
                        @click="rotateCurrentPage(-90)"
                        title="Rotate Left">
                        <i class="fas fa-undo"></i>
                    </b-button>
                    <b-button
                        variant="outline-dark"
                        :disabled="cropMode || reorderMode"
                        @click="rotateCurrentPage(90)"
                        title="Rotate Right">
                        <i class="fas fa-redo"></i>
                    </b-button>
                    <b-button
                        :variant="cropMode ? 'primary' : 'outline-dark'"
                        :disabled="reorderMode"
                        @click="toggleCropMode"
                        title="Crop">
                        <i class="fas fa-crop-alt"></i>
                    </b-button>
                    <b-button
                        v-if="totalPages > 1"
                        :variant="reorderMode ? 'primary' : 'outline-dark'"
                        :disabled="cropMode"
                        @click="toggleReorderMode"
                        title="Reorder Pages">
                        <i class="fas fa-sort"></i>
                    </b-button>
                </b-button-group>

                <!-- Crop mode Apply/Cancel -->
                <template v-if="cropMode">
                    <b-button size="sm" variant="success" class="mr-1" @click="applyCrop">
                        <i class="fas fa-check"></i> Apply Crop
                    </b-button>
                    <b-button size="sm" variant="outline-secondary" class="mr-3" @click="cancelCrop">
                        Cancel
                    </b-button>
                </template>

                <div class="d-flex align-items-center ml-auto">
                    <!-- Page navigation -->
                    <template v-if="totalPages > 1">
                        <b-button
                            variant="outline-dark"
                            size="sm"
                            :disabled="currentPage <= 1 || cropMode"
                            @click="goToPage(currentPage - 1)">
                            <i class="fas fa-chevron-left"></i>
                        </b-button>
                        <span class="mx-2 small">Page {{ currentPage }} of {{ totalPages }}</span>
                        <b-button
                            variant="outline-dark"
                            size="sm"
                            :disabled="currentPage >= totalPages || cropMode"
                            @click="goToPage(currentPage + 1)">
                            <i class="fas fa-chevron-right"></i>
                        </b-button>
                    </template>

                    <PositiveIdGate
                        :enabled="positiveIdEnabled && !!positiveIdTargetId"
                        :enabled-methods="positiveIdMethods"
                        :action-type="PositiveIdActionType.RxAnnotate"
                        :target-entity-type="PositiveIdTargetEntityType.Prescription"
                        :target-entity-id="positiveIdTargetId || ''"
                        @verified="onAnnotationVerified">
                        <template #default="{ requestVerification }">
                            <b-button
                                v-if="hasAnyPermission"
                                variant="primary"
                                class="ml-3 m-0 font-weight-bold"
                                :disabled="saving || !isLoaded"
                                @click="() => requestAnnotationSave(false, requestVerification)">
                                <i v-if="saving" class="fas fa-spinner fa-spin"></i>
                                <i v-else class="fas fa-check"></i>
                                Save
                            </b-button>
                            <b-button
                                v-if="hasAnyPermission && siblingCount > 1"
                                variant="success"
                                class="ml-2 m-0 font-weight-bold"
                                :disabled="saving || !isLoaded"
                                @click="() => requestAnnotationSave(true, requestVerification)">
                                <i v-if="saving" class="fas fa-spinner fa-spin"></i>
                                <i v-else class="fas fa-check"></i>
                                Save to all ({{ siblingCount }})
                            </b-button>
                        </template>
                    </PositiveIdGate>
                </div>
            </div>

            <!-- Reorder Panel -->
            <div v-if="reorderMode" class="reorder-panel mb-2 p-2 bg-white rounded">
                <div class="d-flex justify-content-between align-items-center mb-2">
                    <h6 class="mb-0">Reorder Pages</h6>
                    <div>
                        <b-button size="sm" variant="success" :disabled="!reorderChanged" @click="applyReorder">
                            <i class="fas fa-check"></i> Apply New Order
                        </b-button>
                        <b-button size="sm" variant="outline-secondary" class="ml-1" @click="cancelReorder">
                            Cancel
                        </b-button>
                    </div>
                </div>
                <draggable v-model="reorderPages" class="d-flex flex-wrap">
                    <div v-for="(page, index) in reorderPages" :key="page.originalIndex"
                        class="reorder-thumbnail m-1 text-center"
                        :class="{ 'border-primary': page.originalIndex === currentPage }">
                        <img :src="page.thumbnail" :alt="'Page ' + page.originalIndex" class="thumbnail-img" />
                        <div class="page-number small mt-1">{{ index + 1 }}</div>
                    </div>
                </draggable>
            </div>

            <!-- Canvas Container -->
            <div class="canvas-container-wrapper" ref="canvasWrapper">
                <div class="canvas-container" ref="canvasContainer">
                    <canvas ref="pdfCanvas"></canvas>
                    <canvas ref="fabricCanvas"></canvas>
                </div>
            </div>
        </div>
    </b-modal>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch, Emit } from 'vue-property-decorator';
import axios from 'axios';
import draggable from 'vuedraggable';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import { Permission } from '@/models/Permission';
import { getUserSetting, saveUserSetting } from "@/services/UserSettingsService";
import PositiveIdGate from "@/components/Common/PositiveIdGate.vue";
import { PositiveIdActionType, PositiveIdTargetEntityType, POSITIVE_ID_TOKEN_HEADER } from "@/services/PositiveIdService";
import {
    displayPointToPdf,
    FABRIC_FONT_SIZE_MULT,
    normalizePageRotation,
    resolvePageViewBox,
} from '@/util/pdfPageGeometry';
import { burnAnnotationsIntoPdf as burnAnnotations } from '@/util/pdfAnnotationBurn';
import { ANNOTATION_EXPORT_PROPS, planPageTurn } from '@/util/annotationPageTurn';
import { remapPagesForReorder } from '@/util/annotationPageReorder';
import { resolveAutoPlacedNoteText } from '@/util/hardcopyNoteText';

// Library types (loaded dynamically)
declare const pdfjsLib: any;
declare const fabric: any;
declare const PDFLib: any;

// localStorage key for annotation preferences
const ANNOTATION_PREFERENCES_KEY = 'hardcopy-annotation-preferences';

/** Inset of a note's white backing rect from the note itself, in canvas pixels. */
const ANNOTATION_BACKGROUND_PADDING = 8;

interface AnnotationPreferences {
    strokeColor: string;
    fillColor: string;
    noFill: boolean;
    fontSize: number;
}

interface AnnotationState {
    objects: any[];
}

@Component({
    name: 'PdfAnnotationModal',
    components: { draggable, PositiveIdGate },
})
export default class PdfAnnotationModal extends Vue {
    @Prop({ default: null })
    readonly imageId!: number | null;

    @Prop({ default: null })
    readonly escriptId!: number | null;

    @Prop({ default: 'pdf-annotation-modal' })
    readonly modalId!: string;

    @Prop({ default: 'Annotate PDF' })
    readonly title!: string;

    @Prop({ default: false })
    readonly requirePermission!: boolean;

    @Prop({ default: null })
    readonly initialText!: string | null;

    @Prop({ default: false })
    readonly autoPlace!: boolean;

    @Prop({ default: true })
    readonly showSuccessNotification!: boolean;

    @Prop({ default: 0 })
    readonly siblingCount!: number;

    @Prop({ default: false })
    readonly positiveIdEnabled!: boolean;

    @Prop({ default: null })
    readonly positiveIdMethods!: string[] | null;

    @Prop({ default: null })
    readonly positiveIdTargetId!: string | null;

    /**
     * Prescription this hardcopy belongs to, in `{store}-{rx}-{rf}` form. Supplied so the note text
     * burned into the image is also recorded against the Rx as a searchable note. Null for contexts
     * with no prescription to attach to (an unsaved Rx, or a plain document), where the annotation is
     * burned in but nothing is recorded.
     */
    @Prop({ default: null })
    readonly prescriptionId!: string | null;

    PositiveIdActionType = PositiveIdActionType;
    PositiveIdTargetEntityType = PositiveIdTargetEntityType;
    private verifiedToken = '';
    private pendingSaveToAll = false;

    // State
    private loading = false;
    private saving = false;
    private error = '';
    private isLoaded = false;
    private librariesLoaded = false;

    // PDF state
    private pdfDoc: any = null;
    private pdfBytes: Uint8Array | null = null;
    private currentPage = 1;
    private totalPages = 0;
    private scale = 1.5;

    // Fabric canvas
    private fabricCanvas: any = null;
    private currentTool = 'select';
    private hasSelection = false;

    // Colors
    private strokeColor = '#ff0000';
    private fillColor = '#ffffff';
    private noFill = true;

    // Font size
    private fontSize = 16;
    private readonly fontSizeOptions = [
        { value: 8, text: '8' },
        { value: 10, text: '10' },
        { value: 12, text: '12' },
        { value: 14, text: '14' },
        { value: 16, text: '16' },
        { value: 18, text: '18' },
        { value: 20, text: '20' },
        { value: 24, text: '24' },
        { value: 28, text: '28' },
        { value: 32, text: '32' },
        { value: 36, text: '36' },
        { value: 48, text: '48' },
    ];

    // Rectangle drawing state
    private isDrawingRect = false;
    private rectStartPoint: { x: number; y: number } | null = null;
    private tempRect: any = null;

    // Flag to prevent saving preferences during selection-based UI updates
    private isUpdatingFromSelection = false;

    // Annotations and undo
    private pageAnnotations: Record<number, AnnotationState> = {};
    private undoStacks: Record<number, string[]> = {};
    /**
     * The id of the note auto-placement put on each page, if any. Held so a page rotation can
     * re-place it in the new orientation instead of carrying it to a spot that is no longer blank.
     *
     * An id rather than the object: undo, page switch and rotate all rebuild the canvas through
     * `loadFromJSON`, which discards every Fabric instance. A held reference outlives the object it
     * points at, so it would resurrect a deleted note and let an undone note be carried and
     * re-placed at once.
     */
    private autoPlacedNoteIds: Record<number, string | null> = {};
    private annotationIdSeq = 0;
    private isUndoing = false;
    private readonly MAX_UNDO_STATES = 50;
    private hasUnsavedChanges = false;
    private showUnsavedWarning = false;

    // Edit tools state
    private cropMode = false;
    private cropRect: any = null;
    private cropOverlay: any = null;
    private reorderMode = false;
    private reorderPages: Array<{ originalIndex: number; thumbnail: string }> = [];

    $refs!: {
        canvasWrapper: HTMLDivElement;
        canvasContainer: HTMLDivElement;
        pdfCanvas: HTMLCanvasElement;
        fabricCanvas: HTMLCanvasElement;
    };

    get hasAnnotatePermission(): boolean {
        if (!this.requirePermission) return true;
        return (this.$permissions as Permission[]).some(
            (p) => p.permissionCodeName === 'Maintenance/Hardcopy Note'
        );
    }

    get hasEditPermission(): boolean {
        if (!this.requirePermission) return true;
        return (this.$permissions as Permission[]).some(
            (p) => p.permissionCodeName === 'Maintenance/Edit Image'
        );
    }

    get hasAnyPermission(): boolean {
        return this.hasAnnotatePermission || this.hasEditPermission;
    }

    get reorderChanged(): boolean {
        return this.reorderPages.some((p, i) => p.originalIndex !== i + 1);
    }

    get canUndo(): boolean {
        const stack = this.undoStacks[this.currentPage];
        return stack && stack.length > 1;
    }

    get showFontSizeControl(): boolean {
        if (this.currentTool === 'text') return true;
        const obj = this.fabricCanvas?.getActiveObject();
        return this.isTextObject(obj);
    }

    get currentUserName(): string {
        return (this as any).$user?.userName || 'Unknown';
    }

    private isTextObject(obj: any): boolean {
        return obj && (obj.type === 'i-text' || obj.type === 'text' || obj.type === 'textbox');
    }

    @Emit('saved')
    emitSaved(payload: {
        oldImageId: number | null;
        newImageId: number;
        escriptId: number | null;
        saveToAll: boolean;
        orphanUpdates?: Array<{ storeID: number; rxNumber: number; rfNumber: number; newImageId: number }>;
    }) {
        return payload;
    }

    @Emit('closed')
    emitClosed() {
        return;
    }

    async onModalShow() {
        this.resetState();
        this.loadAnnotationPreferences();
        await this.loadLibraries();
        if (this.librariesLoaded) {
            await this.loadPdf();
        }
        this.setupKeyboardShortcuts();
    }

    onModalHidden() {
        this.cleanup();
        this.emitClosed();
    }

    private resetState() {
        this.loading = false;
        this.saving = false;
        this.error = '';
        this.isLoaded = false;
        this.pdfDoc = null;
        this.pdfBytes = null;
        this.currentPage = 1;
        this.totalPages = 0;
        this.fabricCanvas = null;
        this.currentTool = 'select';
        this.hasSelection = false;
        this.pageAnnotations = {};
        this.undoStacks = {};
        this.autoPlacedNoteIds = {};
        this.annotationIdSeq = 0;
        this.isUndoing = false;
        this.hasUnsavedChanges = false;
        this.showUnsavedWarning = false;
        this.cropMode = false;
        this.cropRect = null;
        this.cropOverlay = null;
        this.reorderMode = false;
        this.reorderPages = [];
    }

    private loadAnnotationPreferences(): void {
        try {
            const saved = getUserSetting(ANNOTATION_PREFERENCES_KEY);
            if (saved) {
                const prefs: AnnotationPreferences = JSON.parse(saved);
                if (prefs.strokeColor) this.strokeColor = prefs.strokeColor;
                if (prefs.fillColor) this.fillColor = prefs.fillColor;
                if (typeof prefs.noFill === 'boolean') this.noFill = prefs.noFill;
                if (prefs.fontSize) this.fontSize = prefs.fontSize;
            }
        } catch (error) {
            console.warn('Failed to load annotation color preferences:', error);
        }
    }

    private saveAnnotationPreferences(): void {
        try {
            const prefs: AnnotationPreferences = {
                strokeColor: this.strokeColor,
                fillColor: this.fillColor,
                noFill: this.noFill,
                fontSize: this.fontSize,
            };
            saveUserSetting(ANNOTATION_PREFERENCES_KEY, JSON.stringify(prefs));
        } catch (error) {
            console.warn('Failed to save annotation color preferences:', error);
        }
    }

    @Watch('strokeColor')
    onStrokeColorChange(): void {
        if (!this.isUpdatingFromSelection) {
            this.saveAnnotationPreferences();
        }
    }

    @Watch('fillColor')
    onFillColorChange(): void {
        if (!this.isUpdatingFromSelection) {
            this.saveAnnotationPreferences();
        }
    }

    @Watch('noFill')
    onNoFillChange(): void {
        if (!this.isUpdatingFromSelection) {
            this.saveAnnotationPreferences();
        }
    }

    @Watch('fontSize')
    onFontSizeChange(): void {
        if (!this.isUpdatingFromSelection) {
            this.saveAnnotationPreferences();
        }
    }

    private cleanup() {
        if (this.fabricCanvas) {
            this.fabricCanvas.dispose();
            this.fabricCanvas = null;
        }
        this.removeKeyboardShortcuts();
    }

    private async loadLibraries(): Promise<void> {
        if (this.librariesLoaded) return;

        const scripts = [
            { src: 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js', global: 'pdfjsLib' },
            { src: 'https://cdnjs.cloudflare.com/ajax/libs/fabric.js/5.3.1/fabric.min.js', global: 'fabric' },
            { src: 'https://unpkg.com/pdf-lib@1.17.1/dist/pdf-lib.min.js', global: 'PDFLib' },
        ];

        try {
            for (const script of scripts) {
                if (!(window as any)[script.global]) {
                    await this.loadScript(script.src);
                }
            }

            // Set PDF.js worker
            if (typeof pdfjsLib !== 'undefined') {
                pdfjsLib.GlobalWorkerOptions.workerSrc =
                    'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
            }

            this.librariesLoaded = true;
        } catch (err) {
            console.error('Failed to load PDF libraries:', err);
            this.error = 'Failed to load PDF annotation libraries.';
        }
    }

    private loadScript(src: string): Promise<void> {
        return new Promise((resolve, reject) => {
            const existing = document.querySelector(`script[src="${src}"]`);
            if (existing) {
                resolve();
                return;
            }

            const script = document.createElement('script');
            script.src = src;
            script.onload = () => resolve();
            script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
            document.head.appendChild(script);
        });
    }

    private async loadPdf(): Promise<void> {
        this.loading = true;
        this.error = '';

        try {
            // Determine the endpoint based on whether we have an imageId or escriptId
            let endpoint: string;
            if (this.escriptId) {
                endpoint = `/api/Escript/${this.escriptId}/image`;
            } else if (this.imageId) {
                endpoint = `/api/Image/${this.imageId}`;
            } else {
                throw new Error('No image ID or eScript ID provided.');
            }

            const response = await axios.get(endpoint, {
                responseType: 'arraybuffer',
            });

            this.pdfBytes = new Uint8Array(response.data);
            this.pdfDoc = await pdfjsLib.getDocument({ data: this.pdfBytes.slice() }).promise;
            this.totalPages = this.pdfDoc.numPages;
            this.pageAnnotations = {};
            this.undoStacks = {};
            this.autoPlacedNoteIds = {};

            // Set loading to false first so Vue renders the canvas elements
            this.loading = false;
            await this.$nextTick();

            await this.renderPage(1);
            this.isLoaded = true;

            // Auto-place initial text if provided
            if (this.initialText && this.autoPlace) {
                await this.$nextTick();
                this.autoPlaceText(this.initialText);
            }
        } catch (err: any) {
            console.error('Error loading PDF:', err);
            this.error = err?.response?.data?.message || err?.message || 'Failed to load PDF.';
            this.loading = false;
        }
    }

    private async renderPage(pageNum: number): Promise<void> {
        if (!this.pdfDoc) return;

        // Save current page annotations before switching
        if (this.fabricCanvas && this.currentPage !== pageNum) {
            this.pageAnnotations[this.currentPage] = this.serializeCanvas();
        }

        const page = await this.pdfDoc.getPage(pageNum);
        const viewport = page.getViewport({ scale: this.scale });

        const pdfCanvas = this.$refs.pdfCanvas;
        const ctx = pdfCanvas.getContext('2d');

        pdfCanvas.width = viewport.width;
        pdfCanvas.height = viewport.height;

        await page.render({
            canvasContext: ctx,
            viewport: viewport,
        }).promise;

        // Initialize/resize Fabric canvas
        this.initFabricCanvas(viewport.width, viewport.height);

        // Load saved annotations for this page. Awaited, because loadFromJSON clears the canvas before
        // repopulating it - anything added while it is still in flight would be wiped.
        if (this.pageAnnotations[pageNum]) {
            await new Promise<void>((resolve) => {
                this.fabricCanvas.loadFromJSON(this.pageAnnotations[pageNum], () => {
                    this.relinkAnnotations();
                    this.fabricCanvas.renderAll();
                    if (!this.undoStacks[pageNum] || this.undoStacks[pageNum].length === 0) {
                        this.$set(this.undoStacks, pageNum, [JSON.stringify(this.serializeCanvas())]);
                    }
                    resolve();
                });
            });
        } else {
            this.$set(this.undoStacks, pageNum, [JSON.stringify(this.serializeCanvas())]);
        }

        this.currentPage = pageNum;
        this.setTool(this.currentTool);
    }

    private initFabricCanvas(width: number, height: number): void {
        const fabricCanvasEl = this.$refs.fabricCanvas;

        if (this.fabricCanvas) {
            this.fabricCanvas.dispose();
        }

        fabricCanvasEl.width = width;
        fabricCanvasEl.height = height;

        this.fabricCanvas = new fabric.Canvas(fabricCanvasEl, {
            width: width,
            height: height,
            selection: true,
        });

        // Position the fabric canvas wrapper
        const wrapper = fabricCanvasEl.parentElement;
        if (wrapper && wrapper.classList.contains('canvas-container')) {
            const fabricWrapper = this.fabricCanvas.wrapperEl;
            if (fabricWrapper) {
                fabricWrapper.style.position = 'absolute';
                fabricWrapper.style.top = '0';
                fabricWrapper.style.left = '0';
            }
        }

        // Event handlers
        this.fabricCanvas.on('mouse:dblclick', (e: any) => {
            if (e.target && this.isTextObject(e.target)) {
                e.target.enterEditing();
            }
        });

        this.fabricCanvas.on('mouse:down', this.onMouseDown.bind(this));
        this.fabricCanvas.on('mouse:move', this.onMouseMove.bind(this));
        this.fabricCanvas.on('mouse:up', this.onMouseUp.bind(this));

        // Track changes for undo
        this.fabricCanvas.on('object:added', () => {
            if (!this.isDrawingRect) {
                this.saveUndoState();
            }
        });
        this.fabricCanvas.on('object:removed', this.saveUndoState.bind(this));
        this.fabricCanvas.on('object:modified', (e: any) => {
            // Mark before snapshotting: the undo state has to carry userPositioned, or undoing some
            // later edit restores the note as "never touched" and the next rotation re-places it.
            this.markNoteUserPositioned(e?.target);
            this.saveUndoState();
        });
        this.fabricCanvas.on('text:changed', this.saveUndoState.bind(this));

        // Selection events
        this.fabricCanvas.on('selection:created', this.onSelectionChanged.bind(this));
        this.fabricCanvas.on('selection:updated', this.onSelectionChanged.bind(this));
        this.fabricCanvas.on('selection:cleared', this.onSelectionChanged.bind(this));
    }

    private onSelectionChanged(): void {
        const obj = this.fabricCanvas?.getActiveObject();
        this.hasSelection = !!obj;

        if (obj) {
            // Prevent @Watch handlers from saving preferences during UI sync
            this.isUpdatingFromSelection = true;

            // Update color pickers to reflect selected object
            if (this.isTextObject(obj)) {
                this.strokeColor = obj.fill || '#000000';
                this.fontSize = obj.fontSize || 16;
            } else if (obj.type === 'rect') {
                this.strokeColor = obj.stroke || '#000000';
                if (obj.fill && obj.fill !== 'transparent') {
                    this.fillColor = obj.fill;
                    this.noFill = false;
                } else {
                    this.noFill = true;
                }
            }

            this.isUpdatingFromSelection = false;
        }
    }

    private getCurrentColors() {
        return {
            stroke: this.strokeColor,
            fill: this.fillColor,
            noFill: this.noFill,
        };
    }

    private addAnnotationStamp(parentObj: any, offsetY: number): void {
        const now = new Date();
        const dateStr = now.toLocaleDateString() + ' ' + now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        const stampText = `${this.currentUserName} - ${dateStr}`;

        // Calculate stamp font size - roughly 60% of parent font size, with floor of 12pt
        const stampFontSize = Math.max(12, Math.round((parentObj.fontSize || 16) * 0.6));

        const stamp = new fabric.Text(stampText, {
            left: parentObj.left,
            top: parentObj.top + offsetY,
            fontSize: stampFontSize,
            fill: '#666666',
            fontFamily: 'Arial',
            fontStyle: 'italic',
            selectable: false,
            evented: false,
            hoverCursor: 'default',
        });

        // Identify the pair in the serialized data, not just in memory: the canvas is torn down and
        // rehydrated on undo, page switch and rotate, and these links have to be rebuildable.
        parentObj.annotationId = parentObj.annotationId || this.nextAnnotationId();
        parentObj.annotationRole = 'note';
        stamp.annotationRole = 'stamp';
        stamp.linkedAnnotationId = parentObj.annotationId;

        // Store reference to stamp on parent and vice versa
        parentObj.annotationStamp = stamp;
        stamp.parentAnnotation = parentObj;
        stamp.stampOffsetY = offsetY;

        this.attachNoteBehavior(parentObj);

        this.fabricCanvas.add(stamp);
    }

    /** Ids only have to be unique within the document being annotated. */
    private nextAnnotationId(): string {
        return `annotation-${++this.annotationIdSeq}`;
    }

    /**
     * Serialize the overlay including the properties the annotation wiring depends on. Fabric writes
     * only the properties it is told about, so anything missing from this list silently disappears on
     * the next `loadFromJSON` — which is how note identity used to be lost.
     */
    private serializeCanvas(): any {
        return this.fabricCanvas.toObject(ANNOTATION_EXPORT_PROPS);
    }

    /**
     * Rebuild what `loadFromJSON` cannot restore. Fabric rehydrates plain data, so the note/stamp/
     * background cross-references and their event handlers have to be re-derived from the serialized
     * ids every time the canvas is repopulated.
     *
     * Without this, editing a note after an undo, a page switch or a rotation leaves the attribution
     * stamp at its old offset and the white backing undersized — and that overlapped state is
     * exactly what gets burned into the saved hardcopy.
     */
    private relinkAnnotations(): void {
        if (!this.fabricCanvas) return;

        const objects = this.fabricCanvas.getObjects();
        const byId = new Map<string, any>();
        objects.forEach((obj: any) => {
            if (obj.annotationId) byId.set(obj.annotationId, obj);
        });

        objects.forEach((obj: any) => {
            const parent = obj.linkedAnnotationId ? byId.get(obj.linkedAnnotationId) : null;
            if (!parent) return;

            if (obj.annotationRole === 'stamp') {
                parent.annotationStamp = obj;
                obj.parentAnnotation = parent;
            } else if (obj.annotationRole === 'background') {
                parent.backgroundRect = obj;
            }
        });

        objects.forEach((obj: any) => {
            if (obj.annotationRole === 'note') this.attachNoteBehavior(obj);
        });
    }

    /**
     * Wire a note so its stamp and white backing follow it. Called both when the note is created and
     * after every rehydration, because fabric restores data but never listeners.
     *
     * The guard flag is deliberately not serialized: a rehydrated note therefore arrives without it
     * and gets wired again, while a live note is never wired twice. Clearing the note's listeners
     * instead would take handlers registered elsewhere with it — the text tool wires its own
     * `changed` to clear its placeholder text before this ever runs.
     */
    private attachNoteBehavior(note: any): void {
        if (note.annotationBehaviorAttached) return;
        note.annotationBehaviorAttached = true;

        // Once the user drags or resizes this note it is no longer "wherever auto-placement happened
        // to put it", so a later rotation carries it through rather than re-placing it.
        note.on('moving', () => {
            note.userPositioned = true;
            this.moveLinkedObjects(note);
        });

        note.on('scaling', () => {
            note.userPositioned = true;
            this.moveLinkedObjects(note);
        });

        note.on('changed', () => {
            this.updateStampPosition(note);
            this.updateBackgroundSize(note);
            this.fabricCanvas?.renderAll();
        });

        note.on('editing:exited', () => {
            this.updateStampPosition(note);
            this.updateBackgroundSize(note);
        });

        note.on('removed', () => {
            if (!this.fabricCanvas) return;
            if (note.annotationStamp) this.fabricCanvas.remove(note.annotationStamp);
            if (note.backgroundRect) this.fabricCanvas.remove(note.backgroundRect);
        });
    }

    /** Keep the stamp and white backing with the note as it is dragged or resized. */
    private moveLinkedObjects(note: any): void {
        if (note.annotationStamp) {
            note.annotationStamp.set({
                left: note.left,
                top: note.top + this.getStampOffset(note),
            });
            note.annotationStamp.setCoords();
        }

        if (note.backgroundRect) {
            note.backgroundRect.set({
                left: note.left - ANNOTATION_BACKGROUND_PADDING,
                top: note.top - ANNOTATION_BACKGROUND_PADDING,
            });
            note.backgroundRect.setCoords();
        }
    }

    /**
     * Flag a note as user-positioned once a modification touched it. Its own `moving` event misses
     * two ways a user positions a note: resizing by a corner handle, and dragging it inside a
     * multi-object selection, where fabric fires on the ActiveSelection rather than on the child.
     */
    private markNoteUserPositioned(target: any): void {
        if (!target) return;

        const touched = typeof target.getObjects === 'function' ? target.getObjects() : [target];
        touched.forEach((obj: any) => {
            if (obj?.annotationRole === 'note') obj.userPositioned = true;
        });
    }

    private getStampOffset(obj: any): number {
        // Use actual height which accounts for multiline text
        const actualHeight = (obj.height || 0) * (obj.scaleY || 1);
        return actualHeight + 2;
    }

    private updateStampPosition(obj: any): void {
        if (obj.annotationStamp && this.fabricCanvas) {
            obj.annotationStamp.set({
                left: obj.left,
                top: obj.top + this.getStampOffset(obj),
            });
            obj.annotationStamp.setCoords();
            this.fabricCanvas.renderAll();
        }
    }

    private updateBackgroundSize(obj: any): void {
        if (!obj.backgroundRect || !this.fabricCanvas) return;

        const padding = ANNOTATION_BACKGROUND_PADDING;
        const actualTextWidth = obj.width * (obj.scaleX || 1);
        const actualTextHeight = obj.height * (obj.scaleY || 1);

        // Get stamp width if available
        const stampWidth = obj.annotationStamp?.width || 200;
        const stampHeight = obj.annotationStamp?.fontSize || 12;

        const totalHeight = actualTextHeight + stampHeight + 6; // Gap between text and stamp
        const bgWidth = Math.max(actualTextWidth, stampWidth) + padding * 2;

        obj.backgroundRect.set({
            left: obj.left - padding,
            top: obj.top - padding,
            width: bgWidth,
            height: totalHeight + padding * 2,
        });
        obj.backgroundRect.setCoords();
    }

    // Auto-placement functionality
    private autoPlaceText(text: string): void {
        if (!this.fabricCanvas || !this.$refs.pdfCanvas) return;

        const pdfCanvas = this.$refs.pdfCanvas as HTMLCanvasElement;
        const canvasWidth = pdfCanvas.width;
        const canvasHeight = pdfCanvas.height;

        // Find blank regions on the page
        const blankRegions = this.findBlankRegions(pdfCanvas);

        // Calculate max width for text (leave margin on sides)
        const maxTextWidth = canvasWidth - 60; // 30px margin on each side
        const charWidth = this.fontSize * 0.6;
        const lineHeightValue = 1.05;

        // Estimate dimensions for placement (Textbox will handle actual wrapping)
        const charsPerLine = Math.floor(maxTextWidth / charWidth);
        const estimatedLines = text.split('\n').reduce((count, line) => {
            return count + Math.max(1, Math.ceil(line.length / charsPerLine));
        }, 0);
        const estimatedWidth = maxTextWidth; // Match the Textbox width used below
        const estimatedHeight = (estimatedLines * this.fontSize * lineHeightValue) + 30; // Include stamp height

        // Find best placement (prioritizes left-aligned, bottom of page)
        const placement = this.findBestPlacement(blankRegions, estimatedWidth, estimatedHeight, canvasWidth, canvasHeight);

        // Create text object with auto-wrapping via Textbox
        const textObj = new fabric.Textbox(text, {
            left: placement.x,
            top: placement.y,
            width: maxTextWidth,
            fontSize: this.fontSize,
            fill: this.strokeColor,
            fontFamily: 'Arial',
            lineHeight: lineHeightValue,
            selectable: true,
        });

        textObj.annotationId = this.nextAnnotationId();
        textObj.annotationRole = 'note';

        this.fabricCanvas.add(textObj);

        // Remember it by id so a page rotation can re-place it in the new orientation. An id rather
        // than the object, because every rehydration of the canvas replaces the instance.
        this.autoPlacedNoteIds[this.currentPage] = textObj.annotationId;

        // Add user stamp first so we can measure its dimensions. This also wires the note's handlers.
        this.addAnnotationStamp(textObj, this.getStampOffset(textObj));

        // If using fallback (no blank space found), add white background
        if (placement.needsBackground) {
            const padding = ANNOTATION_BACKGROUND_PADDING;

            // Calculate actual dimensions from the text object and stamp
            const actualTextWidth = textObj.width || estimatedWidth;
            const actualTextHeight = textObj.height || this.fontSize;
            const stampHeight = textObj.annotationStamp?.height || 16;
            const totalHeight = actualTextHeight + stampHeight + 4; // 4px gap between text and stamp

            // Get actual stamp width from the created stamp object
            const stampWidth = textObj.annotationStamp?.width || 200;
            const bgWidth = Math.max(actualTextWidth, stampWidth) + padding * 2;

            const bgRect = new fabric.Rect({
                left: placement.x - padding,
                top: placement.y - padding,
                width: bgWidth,
                height: totalHeight + padding * 2,
                fill: '#ffffff',
                stroke: '#cccccc',
                strokeWidth: 1,
                selectable: false,
                evented: false,
            });

            bgRect.annotationRole = 'background';
            bgRect.linkedAnnotationId = textObj.annotationId;

            // Insert background behind text and stamp
            this.fabricCanvas.add(bgRect);
            bgRect.sendToBack();

            // Store reference so it moves with text. The handlers that act on it are already
            // attached — they read this link when they fire, so they cover a backing added after.
            textObj.backgroundRect = bgRect;
        }

        // Select the text for editing
        this.fabricCanvas.setActiveObject(textObj);
        this.fabricCanvas.renderAll();

        // Auto-scroll to show the placed text
        this.scrollToObject(textObj);

        this.currentTool = 'select';
        this.hasUnsavedChanges = true;
        this.saveUndoState();
    }

    private scrollToObject(obj: any): void {
        if (!this.$refs.canvasWrapper) return;

        const wrapper = this.$refs.canvasWrapper as HTMLDivElement;
        const objTop = obj.top || 0;
        const objHeight = (obj.height || 0) * (obj.scaleY || 1);

        // Calculate scroll position to center the object in view
        const scrollTarget = objTop - (wrapper.clientHeight / 2) + (objHeight / 2);

        wrapper.scrollTo({
            top: Math.max(0, scrollTarget),
            behavior: 'smooth',
        });
    }

    private findBlankRegions(canvas: HTMLCanvasElement): Array<{ x: number; y: number; width: number; height: number }> {
        const ctx = canvas.getContext('2d');
        if (!ctx) return [];

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const pixels = imageData.data;

        const cellSize = 20; // Grid cell size in pixels
        const cols = Math.floor(canvas.width / cellSize);
        const rows = Math.floor(canvas.height / cellSize);

        // Build grid of blank/occupied cells
        const grid: boolean[][] = [];
        for (let row = 0; row < rows; row++) {
            grid[row] = [];
            for (let col = 0; col < cols; col++) {
                grid[row][col] = this.isCellBlank(pixels, col, row, cellSize, canvas.width);
            }
        }

        // Find contiguous blank rectangles using a simple algorithm
        return this.findBlankRectangles(grid, cellSize, cols, rows);
    }

    private isCellBlank(pixels: Uint8ClampedArray, col: number, row: number, cellSize: number, canvasWidth: number): boolean {
        const startX = col * cellSize;
        const startY = row * cellSize;
        const threshold = 230; // Near-white threshold (lowered to catch off-white backgrounds)

        let blankPixels = 0;
        let totalPixels = 0;

        for (let y = startY; y < startY + cellSize && y < pixels.length / (canvasWidth * 4); y++) {
            for (let x = startX; x < startX + cellSize && x < canvasWidth; x++) {
                const i = (y * canvasWidth + x) * 4;
                if (i + 2 < pixels.length) {
                    const r = pixels[i];
                    const g = pixels[i + 1];
                    const b = pixels[i + 2];

                    if (r > threshold && g > threshold && b > threshold) {
                        blankPixels++;
                    }
                    totalPixels++;
                }
            }
        }

        return totalPixels > 0 && (blankPixels / totalPixels) > 0.90; // 90% blank = blank cell
    }

    private findBlankRectangles(grid: boolean[][], cellSize: number, cols: number, rows: number): Array<{ x: number; y: number; width: number; height: number }> {
        const regions: Array<{ x: number; y: number; width: number; height: number }> = [];
        const visited: boolean[][] = grid.map(row => row.map(() => false));

        for (let startRow = 0; startRow < rows; startRow++) {
            for (let startCol = 0; startCol < cols; startCol++) {
                if (grid[startRow][startCol] && !visited[startRow][startCol]) {
                    // Find max width of blank cells starting from this position
                    let maxWidth = 0;
                    for (let col = startCol; col < cols && grid[startRow][col] && !visited[startRow][col]; col++) {
                        maxWidth++;
                    }

                    // Find max height with this width
                    let maxHeight = 1;
                    for (let row = startRow + 1; row < rows; row++) {
                        let rowValid = true;
                        for (let col = startCol; col < startCol + maxWidth; col++) {
                            if (!grid[row][col] || visited[row][col]) {
                                rowValid = false;
                                break;
                            }
                        }
                        if (rowValid) {
                            maxHeight++;
                        } else {
                            break;
                        }
                    }

                    // Mark cells as visited
                    for (let row = startRow; row < startRow + maxHeight; row++) {
                        for (let col = startCol; col < startCol + maxWidth; col++) {
                            visited[row][col] = true;
                        }
                    }

                    // Only add if region is large enough (at least 3x3 cells)
                    if (maxWidth >= 3 && maxHeight >= 2) {
                        regions.push({
                            x: startCol * cellSize,
                            y: startRow * cellSize,
                            width: maxWidth * cellSize,
                            height: maxHeight * cellSize,
                        });
                    }
                }
            }
        }

        return regions;
    }

    private findBestPlacement(
        _regions: Array<{ x: number; y: number; width: number; height: number }>,
        textWidth: number,
        textHeight: number,
        canvasWidth: number,
        canvasHeight: number
    ): { x: number; y: number; needsBackground: boolean } {
        // Calculate total height needed (text + stamp + box padding)
        const bottomMargin = 20;
        const boxPadding = 16; // 8px padding on top and bottom of background box
        const stampHeight = 20; // Approximate stamp height with spacing
        const totalHeight = textHeight + boxPadding + stampHeight;
        const maxY = canvasHeight - totalHeight - bottomMargin;

        // Width needed for the annotation (text + stamp width, whichever is larger)
        const neededWidth = Math.max(textWidth, 250) + boxPadding * 2; // 250 is approx stamp width

        // Find a blank area that can fit the annotation
        const blankArea = this.findBlankArea(20, neededWidth, totalHeight, canvasWidth, canvasHeight);

        if (blankArea) {
            return {
                x: 20,
                y: blankArea.y,
                needsBackground: !blankArea.isClean, // Add background if area has some content
            };
        }

        // Fallback: place at the very bottom with background
        return {
            x: 20,
            y: Math.max(maxY, 20),
            needsBackground: true,
        };
    }

    private findBlankArea(
        startX: number,
        neededWidth: number,
        neededHeight: number,
        canvasWidth: number,
        canvasHeight: number
    ): { y: number; isClean: boolean } | null {
        const pdfCanvas = this.$refs.pdfCanvas as HTMLCanvasElement;
        if (!pdfCanvas) return null;

        const ctx = pdfCanvas.getContext('2d');
        if (!ctx) return null;

        const imageData = ctx.getImageData(0, 0, canvasWidth, canvasHeight);
        const pixels = imageData.data;
        const threshold = 248; // Very high threshold to catch even light gray

        // Scan from top to find where main content starts, then find blank areas after
        // First, find where the main content block ends
        let mainContentEndY = 0;
        let consecutiveBlankRows = 0;
        const blankRowsNeeded = 10; // Need 10 consecutive blank rows to consider content ended

        for (let y = 0; y < canvasHeight; y++) {
            const rowHasContent = this.rowHasContent(pixels, y, startX, Math.min(startX + neededWidth, canvasWidth - 10), canvasWidth, threshold);

            if (rowHasContent) {
                mainContentEndY = y;
                consecutiveBlankRows = 0;
            } else {
                consecutiveBlankRows++;
                // Once we've found the main content and have enough blank rows, look for placement
                if (mainContentEndY > 50 && consecutiveBlankRows >= blankRowsNeeded) {
                    // Check if we can fit the annotation here
                    const checkY = mainContentEndY + 15; // 15px margin below content
                    if (checkY + neededHeight <= canvasHeight - 20) {
                        const areaIsClean = this.checkAreaIsBlank(
                            pixels, checkY, startX,
                            Math.min(startX + neededWidth, canvasWidth - 10),
                            neededHeight, canvasWidth, threshold
                        );
                        if (areaIsClean) {
                            return { y: checkY, isClean: true };
                        }
                    }
                }
            }
        }

        // If we found main content, place below it
        if (mainContentEndY > 0) {
            const placementY = mainContentEndY + 20;
            if (placementY + neededHeight <= canvasHeight - 20) {
                return { y: placementY, isClean: false };
            }
        }

        return null;
    }

    private rowHasContent(
        pixels: Uint8ClampedArray,
        y: number,
        startX: number,
        endX: number,
        canvasWidth: number,
        threshold: number
    ): boolean {
        for (let x = startX; x < endX; x += 2) {
            const i = (y * canvasWidth + x) * 4;
            if (i + 2 < pixels.length) {
                const r = pixels[i];
                const g = pixels[i + 1];
                const b = pixels[i + 2];
                if (r < threshold || g < threshold || b < threshold) {
                    return true;
                }
            }
        }
        return false;
    }

    private checkAreaIsBlank(
        pixels: Uint8ClampedArray,
        startY: number,
        startX: number,
        endX: number,
        height: number,
        canvasWidth: number,
        threshold: number
    ): boolean {
        for (let y = startY; y < startY + height && y < pixels.length / (canvasWidth * 4); y += 3) {
            if (this.rowHasContent(pixels, y, startX, endX, canvasWidth, threshold)) {
                return false;
            }
        }
        return true;
    }

    private findContentEndY(canvasWidth: number, canvasHeight: number): number {
        const pdfCanvas = this.$refs.pdfCanvas as HTMLCanvasElement;
        if (!pdfCanvas) return canvasHeight - 100;

        const ctx = pdfCanvas.getContext('2d');
        if (!ctx) return canvasHeight - 100;

        const imageData = ctx.getImageData(0, 0, canvasWidth, canvasHeight);
        const pixels = imageData.data;
        const threshold = 245; // Higher threshold to catch lighter gray text/annotations
        const sampleStepX = 3; // Sample every 3 pixels horizontally to catch thin text
        const sampleStepY = 2; // Sample every 2 pixels vertically for accuracy

        // Scan from bottom up to find where content ends
        // Check nearly full width to catch annotations anywhere on the page
        for (let y = canvasHeight - 10; y >= 0; y -= sampleStepY) {
            let hasContent = false;

            // Scan almost full width (leave small margins)
            for (let x = 10; x < canvasWidth - 10; x += sampleStepX) {
                const i = (y * canvasWidth + x) * 4;
                if (i + 2 < pixels.length) {
                    const r = pixels[i];
                    const g = pixels[i + 1];
                    const b = pixels[i + 2];

                    // If pixel is not near-white, it's content
                    // Using higher threshold to catch gray text and annotations
                    if (r < threshold || g < threshold || b < threshold) {
                        hasContent = true;
                        break;
                    }
                }
            }

            if (hasContent) {
                // Found content, return position just below it
                return Math.min(y + 25, canvasHeight - 50);
            }
        }

        // No content found (blank page), place near top
        return 50;
    }

    private onMouseDown(e: any): void {
        if (!this.hasAnnotatePermission || this.cropMode) return;

        // For text tool, allow placing text anywhere (even on top of other objects)
        if (this.currentTool === 'text') {
            // If clicking on an existing text object, let Fabric handle it for editing
            if (e.target && this.isTextObject(e.target)) {
                return;
            }

            // Deselect any current selection
            this.fabricCanvas.discardActiveObject();

            const colors = this.getCurrentColors();
            const pointer = this.fabricCanvas.getPointer(e.e);
            const canvasW = this.fabricCanvas.width || 800;
            const textWidth = Math.max(200, canvasW - pointer.x - 30);
            const text = new fabric.Textbox('Click to edit', {
                left: pointer.x,
                top: pointer.y,
                width: textWidth,
                fontSize: this.fontSize,
                fill: colors.stroke,
                fontFamily: 'Arial',
                lineHeight: 1.05,
                selectable: true,
            });

            // Clear placeholder text when user starts typing and update stamp position for multiline
            text.on('changed', () => {
                if (text.text === 'Click to edit') {
                    text.text = '';
                }
                // Update stamp position when text content changes (e.g., multiline)
                this.updateStampPosition(text);
                this.fabricCanvas.renderAll();
            });

            // Also clear on first keydown if placeholder is still there
            text.on('editing:entered', () => {
                if (text.text === 'Click to edit') {
                    text.selectAll();
                }
            });

            // Update stamp when exiting edit mode (final position)
            text.on('editing:exited', () => {
                this.updateStampPosition(text);
            });

            this.fabricCanvas.add(text);

            // Add username/datetime stamp linked to this text
            this.addAnnotationStamp(text, this.fontSize + 2);

            this.fabricCanvas.setActiveObject(text);
            text.enterEditing();

            // Switch to select tool after placing text
            this.setTool('select');
        } else if (this.currentTool === 'rect' && !e.target) {
            const colors = this.getCurrentColors();
            this.isDrawingRect = true;
            this.rectStartPoint = this.fabricCanvas.getPointer(e.e);
            this.tempRect = new fabric.Rect({
                left: this.rectStartPoint.x,
                top: this.rectStartPoint.y,
                width: 0,
                height: 0,
                fill: colors.noFill ? 'transparent' : colors.fill,
                stroke: colors.stroke,
                strokeWidth: 2,
                selectable: true,
                evented: true,
            });
            this.fabricCanvas.add(this.tempRect);
            this.fabricCanvas.selection = false;
        }
    }

    private onMouseMove(e: any): void {
        if (!this.isDrawingRect || !this.tempRect || !this.rectStartPoint) return;

        const pointer = this.fabricCanvas.getPointer(e.e);
        const left = Math.min(pointer.x, this.rectStartPoint.x);
        const top = Math.min(pointer.y, this.rectStartPoint.y);
        const width = Math.abs(pointer.x - this.rectStartPoint.x);
        const height = Math.abs(pointer.y - this.rectStartPoint.y);

        this.tempRect.set({ left, top, width, height });
        this.fabricCanvas.renderAll();
    }

    private onMouseUp(): void {
        if (this.isDrawingRect && this.tempRect) {
            if (this.tempRect.width < 5 && this.tempRect.height < 5) {
                this.fabricCanvas.remove(this.tempRect);
            } else {
                this.tempRect.setCoords();
                this.fabricCanvas.setActiveObject(this.tempRect);

                this.saveUndoState();
            }

            this.isDrawingRect = false;
            this.rectStartPoint = null;
            this.tempRect = null;
            this.fabricCanvas.selection = true;
            this.fabricCanvas.renderAll();
        }
    }

    private setTool(tool: string): void {
        this.currentTool = tool;
        this.isDrawingRect = false;
        this.rectStartPoint = null;
        this.tempRect = null;

        if (!this.fabricCanvas) return;

        this.fabricCanvas.selection = true;

        if (tool === 'select') {
            this.fabricCanvas.defaultCursor = 'default';
            this.fabricCanvas.hoverCursor = 'move';
        } else {
            this.fabricCanvas.defaultCursor = 'crosshair';
            this.fabricCanvas.hoverCursor = 'crosshair';
        }
    }

    private deleteSelected(): void {
        if (!this.fabricCanvas || !this.hasAnnotatePermission) return;

        const activeObjects = this.fabricCanvas.getActiveObjects();
        if (activeObjects.length > 0) {
            activeObjects.forEach((obj: any) => {
                this.fabricCanvas.remove(obj);
            });
            this.fabricCanvas.discardActiveObject();
            this.fabricCanvas.renderAll();
        }
    }

    private applyColorToSelection(): void {
        const obj = this.fabricCanvas?.getActiveObject();
        if (!obj) return;

        if (this.isTextObject(obj)) {
            obj.set('fill', this.strokeColor);
        } else {
            obj.set('stroke', this.strokeColor);
        }
        this.fabricCanvas.renderAll();
        this.saveUndoState();
    }

    private applyFillToSelection(): void {
        const obj = this.fabricCanvas?.getActiveObject();
        if (!obj || obj.type !== 'rect') return;

        obj.set('fill', this.noFill ? 'transparent' : this.fillColor);
        this.fabricCanvas.renderAll();
        this.saveUndoState();
    }

    private applyFontSizeToSelection(): void {
        const obj = this.fabricCanvas?.getActiveObject();
        if (!obj || !this.isTextObject(obj)) return;

        obj.set('fontSize', this.fontSize);
        this.fabricCanvas.renderAll();

        // Update stamp font size and position after parent font size change
        if (obj.annotationStamp) {
            const newStampFontSize = Math.max(12, Math.round(this.fontSize * 0.6));
            obj.annotationStamp.set('fontSize', newStampFontSize);
        }
        this.updateStampPosition(obj);

        this.saveUndoState();
    }

    // Layer functions
    private bringToFront(): void {
        const obj = this.fabricCanvas?.getActiveObject();
        if (obj) {
            this.fabricCanvas.bringToFront(obj);
            this.fabricCanvas.renderAll();
            this.saveUndoState();
        }
    }

    private bringForward(): void {
        const obj = this.fabricCanvas?.getActiveObject();
        if (obj) {
            this.fabricCanvas.bringForward(obj);
            this.fabricCanvas.renderAll();
            this.saveUndoState();
        }
    }

    private sendBackward(): void {
        const obj = this.fabricCanvas?.getActiveObject();
        if (obj) {
            this.fabricCanvas.sendBackward(obj);
            this.fabricCanvas.renderAll();
            this.saveUndoState();
        }
    }

    private sendToBack(): void {
        const obj = this.fabricCanvas?.getActiveObject();
        if (obj) {
            this.fabricCanvas.sendToBack(obj);
            this.fabricCanvas.renderAll();
            this.saveUndoState();
        }
    }

    // Undo functionality
    private saveUndoState(): void {
        if (!this.fabricCanvas || this.isUndoing) return;

        if (!this.undoStacks[this.currentPage]) {
            this.$set(this.undoStacks, this.currentPage, []);
        }

        const state = JSON.stringify(this.serializeCanvas());
        const stack = this.undoStacks[this.currentPage];

        if (stack.length > 0 && stack[stack.length - 1] === state) {
            return;
        }

        // Mark as having unsaved changes if this isn't the initial state
        if (stack.length > 0) {
            this.hasUnsavedChanges = true;
        }

        stack.push(state);

        if (stack.length > this.MAX_UNDO_STATES) {
            stack.shift();
        }
    }

    private undo(): void {
        if (!this.fabricCanvas || this.isUndoing) return;

        const stack = this.undoStacks[this.currentPage];
        if (!stack || stack.length <= 1) {
            return;
        }

        this.isUndoing = true;
        stack.pop();
        const previousState = stack[stack.length - 1];

        const promise = this.fabricCanvas.loadFromJSON(previousState, () => {
            this.relinkAnnotations();
            this.fabricCanvas.renderAll();
        });

        // Fabric.js v5 loadFromJSON returns a Promise
        if (promise && typeof promise.then === 'function') {
            promise.then(() => {
                this.isUndoing = false;
            }).catch(() => {
                this.isUndoing = false;
            });
        } else {
            this.isUndoing = false;
        }
    }

    private goToPage(pageNum: number): void {
        if (pageNum >= 1 && pageNum <= this.totalPages) {
            this.renderPage(pageNum);
        }
    }

    // Keyboard shortcuts
    private keydownHandler: ((e: KeyboardEvent) => void) | null = null;

    private setupKeyboardShortcuts(): void {
        this.keydownHandler = (e: KeyboardEvent) => {
            // Don't intercept any keys when editing text
            const activeObject = this.fabricCanvas?.getActiveObject();
            if (activeObject && this.isTextObject(activeObject) && activeObject.isEditing) {
                return;
            }

            // Ctrl+Z for undo
            if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
                e.preventDefault();
                this.undo();
                return;
            }

            // Delete for delete
            if ((e.key === 'Delete' || e.key === 'Backspace') && this.fabricCanvas) {
                if (activeObject) {
                    e.preventDefault();
                    this.deleteSelected();
                }
            }
        };

        document.addEventListener('keydown', this.keydownHandler);
    }

    private removeKeyboardShortcuts(): void {
        if (this.keydownHandler) {
            document.removeEventListener('keydown', this.keydownHandler);
            this.keydownHandler = null;
        }
    }

    // Remove placeholder text objects that weren't edited
    private removePlaceholderTexts(): void {
        if (!this.fabricCanvas) return;

        const objects = this.fabricCanvas.getObjects();
        const toRemove: any[] = [];

        for (const obj of objects) {
            if (this.isTextObject(obj) && obj.text === 'Click to edit') {
                toRemove.push(obj);
            }
        }

        // Remove each placeholder (event handlers will clean up stamps and backgrounds)
        for (const obj of toRemove) {
            this.fabricCanvas.remove(obj);
        }

        if (toRemove.length > 0) {
            this.fabricCanvas.renderAll();
        }
    }

    /**
     * Flatten annotations into a PDF and return the modified bytes. The coordinate mapping lives in
     * util/pdfAnnotationBurn; this supplies the runtime libraries it needs.
     */
    private burnAnnotationsIntoPdf(
        pdfBytes: Uint8Array,
        pageAnnotations: Record<number, AnnotationState>,
        scale: number
    ): Promise<Uint8Array> {
        return burnAnnotations(PDFLib, pdfBytes, pageAnnotations, scale, {
            fontSizeMult: fabric?.Text?.prototype?._fontSizeMult || FABRIC_FONT_SIZE_MULT,
        });
    }

    /**
     * For each orphaned sibling (prescription on a derived image in the same lineage),
     * fetches their current PDF, burns the same annotations on top, uploads as a new image,
     * and returns the mapping of prescription → new image ID.
     */
    private async processOrphanedSiblings(
        pageAnnotations: Record<number, AnnotationState>,
        scale: number
    ): Promise<Array<{ storeID: number; rxNumber: number; rfNumber: number; newImageId: number }>> {
        const orphanResponse = await axios.get<Array<{ storeID: number; rxNumber: number; rfNumber: number; imageID: number }>>(
            `/Prescription/orphaned-siblings/${this.imageId}`
        );

        const orphans = orphanResponse.data;
        if (!orphans.length) return [];

        const updates: Array<{ storeID: number; rxNumber: number; rfNumber: number; newImageId: number }> = [];

        // Group by imageID so we re-annotate each unique derived image only once
        const byImage = new Map<number, Array<{ storeID: number; rxNumber: number; rfNumber: number }>>();
        for (const o of orphans) {
            const list = byImage.get(o.imageID) || [];
            list.push({ storeID: o.storeID, rxNumber: o.rxNumber, rfNumber: o.rfNumber });
            byImage.set(o.imageID, list);
        }

        for (const [orphanImageId, prescriptions] of byImage) {
            // Fetch the orphan's current PDF
            const pdfResponse = await axios.get(`/api/Image/${orphanImageId}`, { responseType: 'arraybuffer' });
            const orphanPdfBytes = new Uint8Array(pdfResponse.data);

            // Burn the same annotations on top of their existing content
            const annotatedBytes = await this.burnAnnotationsIntoPdf(orphanPdfBytes, pageAnnotations, scale);

            // TODO: Record the hardcopy note against these siblings too. Each one is annotated with the
            // same text, but only the primary Rx gets a note row, so a sibling shows the annotation on
            // its hardcopy with nothing searchable behind it. The identities are already in hand here;
            // what is missing is a way to name several of them on one upload, since a derived image can
            // serve more than one prescription and the save takes a single rxId.
            // Upload as a new image derived from the orphan's current image
            const formData = new FormData();
            formData.append('files', new Blob([annotatedBytes], { type: 'application/pdf' }), 'annotated.pdf');
            const response = await axios.post(
                `/api/Image/SaveDocument?bypassIngestion=true&sourceImageId=${orphanImageId}`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            const newImageId = (response.data as number[])[0];
            for (const rx of prescriptions) {
                updates.push({ ...rx, newImageId });
            }
        }

        return updates;
    }

    // Fail closed: when positive-ID is required but no Rx target exists (unsaved Rx), block rather than
    // save ungated — an annotation with no auditable target can't be recorded.
    private requestAnnotationSave(saveToAll: boolean, requestVerification: () => void): void {
        if (this.positiveIdEnabled && !this.positiveIdTargetId) {
            this.$notification(NotificationOptions.error('Positive identification requires a saved prescription — save the Rx before annotating.'));
            return;
        }
        this.pendingSaveToAll = saveToAll;
        requestVerification();
    }

    // Captures the verified token from PositiveIdGate and continues the annotation save.
    private onAnnotationVerified(payload: { verifiedToken: string }): void {
        this.verifiedToken = payload.verifiedToken;
        void this.saveAnnotatedPdf(this.pendingSaveToAll);
    }

    // Save functionality
    private async saveAnnotatedPdf(saveToAll = false): Promise<void> {
        if (!this.pdfBytes || !this.hasAnyPermission) return;

        this.saving = true;

        try {
            // Remove any placeholder text objects that weren't edited
            this.removePlaceholderTexts();

            // Save current page annotations
            if (this.fabricCanvas) {
                this.pageAnnotations[this.currentPage] = this.serializeCanvas();
            }

            // Burn annotations into the PDF
            const modifiedPdfBytes = await this.burnAnnotationsIntoPdf(this.pdfBytes!, this.pageAnnotations, this.scale);
            const blob = new Blob([modifiedPdfBytes], { type: 'application/pdf' });

            // The note text as it stands after any in-modal edits, so the recorded note and the
            // hardcopy say the same thing. Rides this request rather than a call of its own: a
            // positive-id token is single-use, so a second gated call would be rejected as a replay.
            const annotationNote = resolveAutoPlacedNoteText(this.pageAnnotations, this.autoPlacedNoteIds);

            // Upload to API - different endpoint for eScript vs regular image
            const formData = new FormData();

            // Verified => gated endpoints (filter enforces the token + records the RxAnnotate event); else standard.
            const gated = !!this.verifiedToken;
            const headers: Record<string, string> = { 'Content-Type': 'multipart/form-data' };
            if (gated) headers[POSITIVE_ID_TOKEN_HEADER] = this.verifiedToken;

            const appendRxAndNote = () => {
                // The gated endpoints bind their positive-id target to rxId, so that field carries
                // the verified id there whether or not a note rides along with it.
                if (gated) formData.append('rxId', this.positiveIdTargetId || '');

                // Either way the note is only filed when there is a saved Rx to file it against, and
                // prescriptionId is what says so. On the gated path the note lands on the
                // positively-identified Rx, which is the one the audit event names.
                if (!annotationNote || !this.prescriptionId) return;
                if (!gated) formData.append('rxId', this.prescriptionId);
                formData.append('annotationNote', annotationNote);
            };

            if (this.escriptId) {
                // For eScripts, update the existing eScript image
                formData.append('file', blob, 'annotated.pdf');
                appendRxAndNote();
                const escriptUrl = gated
                    ? `/api/Escript/${this.escriptId}/annotated-image`
                    : `/api/Escript/${this.escriptId}/image`;
                await axios.put(escriptUrl, formData, { headers });

                this.hasUnsavedChanges = false;
                if (this.showSuccessNotification) {
                    this.$notification(NotificationOptions.success('eScript annotation saved successfully.'));
                }
                this.emitSaved({ oldImageId: null, newImageId: 0, escriptId: this.escriptId, saveToAll: false });
                this.$bvModal.hide(this.modalId);
            } else {
                // For regular images, create a new image document
                formData.append('files', blob, 'annotated.pdf');
                appendRxAndNote();
                const sourceParam = this.imageId ? `&sourceImageId=${this.imageId}` : '';
                const gatedSourceParam = this.imageId ? `?sourceImageId=${this.imageId}` : '';
                const imageUrl = gated
                    ? `/api/Image/SaveAnnotationDocument${gatedSourceParam}`
                    : `/api/Image/SaveDocument?bypassIngestion=true${sourceParam}`;
                const response = await axios.post(imageUrl, formData, { headers });

                const imageIds = response.data as number[];
                if (imageIds && imageIds.length > 0) {
                    const newImageId = imageIds[0];
                    this.hasUnsavedChanges = false;
                    if (this.showSuccessNotification) {
                        this.$notification(NotificationOptions.success('PDF saved successfully.'));
                    }
                    let orphanUpdates: Array<{ storeID: number; rxNumber: number; rfNumber: number; newImageId: number }> = [];

                    if (saveToAll && this.imageId) {
                        try {
                            orphanUpdates = await this.processOrphanedSiblings(this.pageAnnotations, this.scale);
                        } catch (orphanErr) {
                            console.error('Failed to process orphaned siblings:', orphanErr);
                        }
                    }

                    this.emitSaved({ oldImageId: this.imageId, newImageId, escriptId: null, saveToAll, orphanUpdates });
                    this.$bvModal.hide(this.modalId);
                } else {
                    throw new Error('No image ID returned from server.');
                }
            }
        } catch (err: any) {
            console.error('Error saving PDF:', err);
            this.$notification(
                NotificationOptions.error(err?.response?.data?.message || err?.message || 'Failed to save PDF.')
            );
        } finally {
            this.saving = false;
        }
    }

    // === Edit Tools: Rotate ===

    private async rotateCurrentPage(degrees: number): Promise<void> {
        if (!this.pdfBytes || !this.fabricCanvas) return;

        try {
            // Capture the overlay as it stands before the canvas is rebuilt at the turned size.
            const displayWidth = this.fabricCanvas.width || 0;
            const displayHeight = this.fabricCanvas.height || 0;

            // What carries and what is re-placed is decided over the serialized objects, away from
            // the canvas, so the decision is unit tested rather than inferred from live references.
            const plan = planPageTurn(
                this.fabricCanvas.getObjects().map((obj: any) => obj.toObject(ANNOTATION_EXPORT_PROPS)),
                {
                    turnDegrees: degrees,
                    displayWidth,
                    displayHeight,
                    autoPlacedNoteId: this.autoPlacedNoteIds[this.currentPage] || null,
                }
            );

            const { PDFDocument, degrees: pdfDegrees } = PDFLib;
            const pdfLibDoc = await PDFDocument.load(this.pdfBytes, { ignoreEncryption: true });
            const page = pdfLibDoc.getPage(this.currentPage - 1);
            const currentRotation = page.getRotation().angle;
            // Normalize so /Rotate is never persisted negative or past a full turn - rotating left
            // used to store -90, which downstream rotation checks did not recognize.
            page.setRotation(pdfDegrees(normalizePageRotation(currentRotation + degrees)));

            this.pdfBytes = new Uint8Array(await pdfLibDoc.save());

            // renderPage reloads the page's annotations from here once the canvas is resized.
            this.$set(this.pageAnnotations, this.currentPage, { objects: plan.carried });
            this.$set(this.undoStacks, this.currentPage, []);
            // A carried note is the user's now; a re-placed one gets a fresh id below.
            this.autoPlacedNoteIds[this.currentPage] = null;

            // Reload and re-render
            this.pdfDoc = await pdfjsLib.getDocument({ data: this.pdfBytes.slice() }).promise;
            await this.renderPage(this.currentPage);

            if (plan.replacedNoteText) {
                await this.$nextTick();
                this.autoPlaceText(plan.replacedNoteText);
            }

            this.hasUnsavedChanges = true;
        } catch (err: any) {
            console.error('Error rotating page:', err);
            this.$notification(NotificationOptions.error('Failed to rotate page.'));
        }
    }

    // === Edit Tools: Crop ===

    private toggleCropMode(): void {
        if (this.cropMode) {
            this.cancelCrop();
        } else {
            this.enterCropMode();
        }
    }

    private enterCropMode(): void {
        if (!this.fabricCanvas) return;

        this.cropMode = true;
        const canvasW = this.fabricCanvas.width || 800;
        const canvasH = this.fabricCanvas.height || 600;

        // Add semi-transparent overlay
        this.cropOverlay = new fabric.Rect({
            left: 0,
            top: 0,
            width: canvasW,
            height: canvasH,
            fill: 'rgba(0, 0, 0, 0.4)',
            selectable: false,
            evented: false,
            excludeFromExport: true,
        });
        this.fabricCanvas.add(this.cropOverlay);

        // Add resizable crop rect (80% centered)
        const margin = 0.1;
        this.cropRect = new fabric.Rect({
            left: canvasW * margin,
            top: canvasH * margin,
            width: canvasW * (1 - 2 * margin),
            height: canvasH * (1 - 2 * margin),
            fill: 'rgba(255, 255, 255, 0.3)',
            stroke: '#ffffff',
            strokeWidth: 2,
            strokeDashArray: [5, 5],
            lockRotation: true,
            hasRotatingPoint: false,
            cornerColor: '#ffffff',
            cornerSize: 10,
            transparentCorners: false,
            excludeFromExport: true,
        });
        this.fabricCanvas.add(this.cropRect);
        this.fabricCanvas.setActiveObject(this.cropRect);
        this.fabricCanvas.renderAll();
    }

    private async applyCrop(): Promise<void> {
        if (!this.pdfBytes || !this.cropRect || !this.fabricCanvas) return;

        try {
            // Warn about annotations
            const pageAnns = this.pageAnnotations[this.currentPage];
            if (pageAnns && pageAnns.objects && pageAnns.objects.length > 0) {
                const confirmed = await this.$bvModal.msgBoxConfirm(
                    'Cropping this page will clear its annotations. Continue?',
                    { title: 'Clear Annotations?', okVariant: 'warning', okTitle: 'Crop & Clear' }
                );
                if (!confirmed) return;
            }

            const canvasW = this.fabricCanvas.width || 800;
            const canvasH = this.fabricCanvas.height || 600;

            // Get crop rect bounds (accounting for scale) and clamp to canvas
            const cropLeft = Math.max(0, this.cropRect.left);
            const cropTop = Math.max(0, this.cropRect.top);
            const cropWidth = Math.min(this.cropRect.width * (this.cropRect.scaleX || 1), canvasW - cropLeft);
            const cropHeight = Math.min(this.cropRect.height * (this.cropRect.scaleY || 1), canvasH - cropTop);

            const { PDFDocument } = PDFLib;
            const pdfLibDoc = await PDFDocument.load(this.pdfBytes, { ignoreEncryption: true });
            const page = pdfLibDoc.getPage(this.currentPage - 1);

            // setCropBox/setMediaBox operate in the page's unrotated coordinate space, while the
            // canvas shows the rotated view of the box currently being rendered. Map both corners of
            // the crop rect back and take the extremes. Going through the current view box - rather
            // than assuming an origin of (0,0) - is what keeps a second crop of an already-cropped
            // page from landing outside the page.
            const rotation = normalizePageRotation(page.getRotation().angle);
            const view = resolvePageViewBox(page);
            const inv = 1 / this.scale;

            const cropStart = displayPointToPdf(cropLeft * inv, cropTop * inv, rotation, view);
            const cropEnd = displayPointToPdf(
                (cropLeft + cropWidth) * inv,
                (cropTop + cropHeight) * inv,
                rotation,
                view
            );

            const pdfCropLeft = Math.min(cropStart.x, cropEnd.x);
            const pdfCropBottom = Math.min(cropStart.y, cropEnd.y);
            const pdfCropWidth = Math.abs(cropEnd.x - cropStart.x);
            const pdfCropHeight = Math.abs(cropEnd.y - cropStart.y);

            page.setCropBox(pdfCropLeft, pdfCropBottom, pdfCropWidth, pdfCropHeight);
            page.setMediaBox(pdfCropLeft, pdfCropBottom, pdfCropWidth, pdfCropHeight);

            this.pdfBytes = new Uint8Array(await pdfLibDoc.save());

            // Clean up crop UI
            this.removeCropObjects();
            this.cropMode = false;

            // Clear annotations for this page
            delete this.pageAnnotations[this.currentPage];
            this.$set(this.undoStacks, this.currentPage, []);
            this.autoPlacedNoteIds[this.currentPage] = null;

            // Reload and re-render
            this.pdfDoc = await pdfjsLib.getDocument({ data: this.pdfBytes.slice() }).promise;
            await this.renderPage(this.currentPage);

            this.hasUnsavedChanges = true;
        } catch (err: any) {
            console.error('Error cropping page:', err);
            this.$notification(NotificationOptions.error('Failed to crop page.'));
        }
    }

    private cancelCrop(): void {
        this.removeCropObjects();
        this.cropMode = false;
    }

    private removeCropObjects(): void {
        if (!this.fabricCanvas) return;
        if (this.cropOverlay) {
            this.fabricCanvas.remove(this.cropOverlay);
            this.cropOverlay = null;
        }
        if (this.cropRect) {
            this.fabricCanvas.remove(this.cropRect);
            this.cropRect = null;
        }
        this.fabricCanvas.discardActiveObject();
        this.fabricCanvas.renderAll();
    }

    // === Edit Tools: Reorder ===

    private async toggleReorderMode(): Promise<void> {
        if (this.reorderMode) {
            this.cancelReorder();
        } else {
            await this.enterReorderMode();
        }
    }

    private async enterReorderMode(): Promise<void> {
        this.reorderMode = true;
        this.reorderPages = [];

        // Save current page annotations before generating thumbnails
        if (this.fabricCanvas) {
            this.pageAnnotations[this.currentPage] = this.serializeCanvas();
        }

        try {
            // Generate thumbnails for all pages
            for (let i = 1; i <= this.totalPages; i++) {
                const thumbnail = await this.generatePageThumbnail(i);
                this.reorderPages.push({ originalIndex: i, thumbnail });
            }
        } catch (err: any) {
            console.error('Error generating page thumbnails:', err);
            this.$notification(NotificationOptions.error('Failed to generate page thumbnails.'));
            this.reorderMode = false;
            this.reorderPages = [];
        }
    }

    private async generatePageThumbnail(pageNum: number): Promise<string> {
        if (!this.pdfDoc) return '';

        const page = await this.pdfDoc.getPage(pageNum);
        const viewport = page.getViewport({ scale: 0.3 });

        const canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return '';

        await page.render({ canvasContext: ctx, viewport }).promise;
        return canvas.toDataURL('image/webp', 0.7);
    }

    private async applyReorder(): Promise<void> {
        if (!this.pdfBytes || !this.reorderChanged) return;

        try {
            const { PDFDocument } = PDFLib;
            const sourcePdf = await PDFDocument.load(this.pdfBytes, { ignoreEncryption: true });
            const newPdf = await PDFDocument.create();

            // Copy pages in the new order
            const newOrder = this.reorderPages.map(p => p.originalIndex - 1);
            const copiedPages = await newPdf.copyPages(sourcePdf, newOrder);
            copiedPages.forEach(page => newPdf.addPage(page));

            // Every map keyed by page number is renumbered here. Missing one does not fail loudly -
            // it leaves that map describing a different page than the one it is filed under.
            const order = this.reorderPages.map(page => page.originalIndex);
            this.pageAnnotations = remapPagesForReorder(this.pageAnnotations, order);
            this.undoStacks = remapPagesForReorder(this.undoStacks, order);
            this.autoPlacedNoteIds = remapPagesForReorder(this.autoPlacedNoteIds, order);

            this.pdfBytes = new Uint8Array(await newPdf.save());
            this.pdfDoc = await pdfjsLib.getDocument({ data: this.pdfBytes.slice() }).promise;
            this.totalPages = this.pdfDoc.numPages;

            this.reorderMode = false;
            this.reorderPages = [];

            // Re-render page 1
            await this.renderPage(1);
            this.hasUnsavedChanges = true;
        } catch (err: any) {
            console.error('Error reordering pages:', err);
            this.$notification(NotificationOptions.error('Failed to reorder pages.'));
        }
    }

    private cancelReorder(): void {
        this.reorderMode = false;
        this.reorderPages = [];
    }

    // Close handling
    private onBeforeHide(bvModalEvt: any): void {
        if (this.hasUnsavedChanges) {
            bvModalEvt.preventDefault();
            this.showUnsavedWarning = true;
        }
    }

    private discardAndClose(): void {
        this.hasUnsavedChanges = false;
        this.showUnsavedWarning = false;
        this.$bvModal.hide(this.modalId);
    }

    // Public method to open the modal
    public show(): void {
        this.$bvModal.show(this.modalId);
    }

    // Public method to hide the modal
    public hide(): void {
        this.$bvModal.hide(this.modalId);
    }
}
</script>

<style scoped>
.canvas-container-wrapper {
    overflow: auto;
    max-height: calc(100vh - 250px);
    border: 1px solid #dee2e6;
    border-radius: 4px;
    background: #f8f9fa;
}

.canvas-container {
    position: relative;
    display: inline-block;
    margin: auto;
}

.canvas-container canvas {
    display: block;
}

.color-picker {
    width: 40px;
    height: 30px;
    padding: 2px;
    border: 1px solid #343a40;
    cursor: pointer;
}

.annotation-toolbar {
    border: 1px solid #343a40;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.annotation-toolbar .btn-group {
    flex-shrink: 0;
}

.annotation-toolbar .btn-outline-dark {
    border-color: #343a40;
    color: #343a40;
}

.annotation-toolbar .btn-outline-dark:hover:not(:disabled) {
    background-color: #343a40;
    color: #fff;
}

.annotation-toolbar .btn-outline-dark:disabled {
    opacity: 0.4;
}

.annotation-toolbar label {
    color: #343a40;
    font-weight: 500;
}

.font-size-select {
    width: 65px;
    border-color: #343a40;
}

.toolbar-separator {
    display: inline-block;
    width: 1px;
    height: 24px;
    background-color: #dee2e6;
    vertical-align: middle;
}

.reorder-panel {
    border: 1px solid #343a40;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    max-height: 200px;
    overflow-y: auto;
}

.reorder-thumbnail {
    cursor: grab;
    border: 2px solid #dee2e6;
    border-radius: 4px;
    padding: 4px;
    background: #f8f9fa;
    transition: border-color 0.15s;
}

.reorder-thumbnail:hover {
    border-color: #6c757d;
}

.reorder-thumbnail.border-primary {
    border-color: #007bff !important;
}

.thumbnail-img {
    max-height: 120px;
    max-width: 90px;
    object-fit: contain;
}

.page-number {
    font-weight: 600;
    color: #495057;
}

</style>
